import {css} from 'aphrodite';
import React, {useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {
  CombineDataFramesState,
  DEFAULT_COMBINE_DATA_FRAMES_STATE,
  getCombineTablesStepParams,
} from 'analysis-tool/data-frame-creation/combine-data-frames/CombineDataFramesState';
import DataFrameNameField from 'analysis-tool/data-frame-creation/steps/DataFrameNameField';
import {
  computeUpdatedTablesState,
  ConfigureCombineMethodContent,
  PairedTableSelector,
  useUnionColumnState,
} from 'analysis-tool/right-sidebar/combine-data-frames/CombineTablesFormComponents';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import CombineTablesMethodSelector from 'pipelines/joins/CombineTablesMethodSelector';
import {StepperV2, stepperStyles} from 'ui-library/stepper/Stepper';

import RightSidebarSkeleton, {RightSidebarWidth} from '../components/RightSidebarSkeleton';
import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';

export default function CombineTablesSourceRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.COMBINE_TABLES_SOURCE>): JSX.Element {
  const [state, setState] = useState<CombineDataFramesState>({
    ...DEFAULT_COMBINE_DATA_FRAMES_STATE,
    combineMethod: selectedRightSidebarItem.initialCombineMethod,
  });
  const [isFinalizing, setIsFinalizing] = useState(false);
  const unionColumnState = useUnionColumnState();

  const {addStepParams} = getCombineTablesStepParams(
    state.combineMethod,
    state.joinType,
    state.leftTable,
    state.rightTable,
    state.relationships,
    unionColumnState.selectedPrimaryColumns,
    unionColumnState.selectedSecondaryColumns,
    state.dataFrameName
  );

  const updateTables: React.ComponentProps<typeof PairedTableSelector>['onUpdateTables'] = (
    leftTable,
    rightTable
  ) => {
    setState((prev) => computeUpdatedTablesState(prev, leftTable, rightTable));
  };

  const finalize = async (): Promise<void> => {
    if (addStepParams == null) {
      return;
    }
    try {
      setIsFinalizing(true);
      await AnalysisActions.addOrReplaceAndRunAnalysisStep(analysis.id, addStepParams);
      onChangeSelectedRightSidebarItem(null);
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <RightSidebarSkeleton
      title="Combine tables"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Add table',
          onClick: finalize,
          dataTestAttrsElement: 'add-table',
          disabled:
            addStepParams == null ||
            isFinalizing ||
            (state.combineMethod === PipelineStepType.UNION_DATASETS && unionColumnState.hasDuplicateColumns),
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      dataTestAttrs={{
        component: 'CombineTablesSourceRightSidebar',
        element: 'root',
      }}
    >
      <StepperV2
        steps={[
          {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select a combination method</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <CombineTablesMethodSelector
                  combineTablesMethod={state.combineMethod}
                  onSelectCombineTablesMethod={(combineMethod) => {
                    setState((prev) => ({...prev, combineMethod}));
                  }}
                />
              </div>
            ),
            key: 'select-combine-method',
          },
          {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Select tables to combine</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <PairedTableSelector
                  analysis={analysis}
                  state={state}
                  onUpdateTables={updateTables}
                  dataTestComponent="CombineTablesSourceRightSidebar"
                />
              </div>
            ),
            key: 'select-tables',
          },
          {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Configure combine method</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <ConfigureCombineMethodContent
                  state={state}
                  onUpdateJoinType={(joinType) => {
                    setState((prev) => ({...prev, joinType}));
                  }}
                  onUpdateRelationships={(relationships) => {
                    setState((prev) => ({...prev, relationships}));
                  }}
                  unionColumnState={unionColumnState}
                />
              </div>
            ),
            key: 'configure-combine-method',
          },
          {
            title: <div className={css(stepperStyles.defaultStepTitle)}>Name table</div>,
            content: (
              <div className={css(stepperStyles.defaultStepContent)}>
                <DataFrameNameField
                  hasUserEdits={state.dataFrameNameHasUserEdits}
                  name={state.dataFrameName}
                  onNameChange={(dataFrameName) => {
                    setState((prev) => ({...prev, dataFrameName, dataFrameNameHasUserEdits: true}));
                  }}
                />
              </div>
            ),
            key: 'name-table',
          },
        ]}
      />
    </RightSidebarSkeleton>
  );
}
